import type { PublicSchoolDto } from '@motoschool/shared';
import { Sheet } from './ui';

/**
 * Окно «Связаться со школой»: единственное место, где собраны способы связи.
 *
 * Вынесено из лендинга, потому что то же окно открывается со страниц курсов:
 * контакты должны быть одни и те же везде, иначе на одной странице окажется
 * телефон, которого нет на другой.
 */

interface Channel {
  key: string;
  href: string;
  label: string;
  hint: string;
}

/** Цифры номера для ссылок `wa.me` и `tel:` — из записи с пробелами и скобками. */
export function phoneDigits(phone: string | null | undefined): string {
  return phone?.replace(/\D/g, '') ?? '';
}

/**
 * Способы связи — из настроек школы, других источников быть не должно:
 * телефон, записанный ещё и в коде, разъезжается с админкой молча, и на
 * сайте месяцами висит номер, по которому уже никто не отвечает.
 *
 * Телефон один и на звонок, и на WhatsApp: в ссылку идут только цифры,
 * человеку показывается привычная запись с пробелами. Пустые поля просто
 * не дают строк — ссылок в никуда в окне не появится.
 */
export function contactChannels(school: PublicSchoolDto | undefined): Channel[] {
  if (!school) return [];

  const channels: Channel[] = [];
  const digits = phoneDigits(school.contactPhone);

  if (digits) {
    channels.push({
      key: 'whatsapp',
      href: `https://wa.me/${digits}`,
      label: 'WhatsApp',
      hint: school.contactPhone ?? '',
    });
  }
  if (school.contactInstagram) {
    channels.push({
      key: 'instagram',
      href: `https://www.instagram.com/${school.contactInstagram}`,
      label: 'Instagram',
      hint: `@${school.contactInstagram}`,
    });
  }
  if (digits) {
    channels.push({
      key: 'phone',
      href: `tel:+${digits}`,
      label: 'Позвонить',
      hint: school.contactPhone ?? '',
    });
  }

  return channels;
}

export function ContactSheet({
  school,
  isOpen,
  onClose,
}: {
  school: PublicSchoolDto | undefined;
  isOpen: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const channels = contactChannels(school);

  return (
    <Sheet isOpen={isOpen} title="Связаться со школой" onClose={onClose}>
      {channels.length === 0 ? (
        <p className="text-sm text-muted">
          Контакты пока не заполнены. Администратор задаёт их в настройках школы.
        </p>
      ) : (
        <div className="space-y-2">
          {channels.map((channel) => (
            <a
              key={channel.key}
              href={channel.href}
              // Мессенджеры открываем отдельной вкладкой, звонок — нет:
              // tel: обрабатывает система, и пустая вкладка была бы мусором.
              {...(channel.href.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}
              className="flex min-h-16 items-center justify-between gap-3 rounded-xl border border-line bg-surface px-4 py-3 transition-colors hover:border-brand-500 hover:bg-brand-50"
            >
              <span className="min-w-0">
                <span className="block font-semibold">{channel.label}</span>
                <span className="block truncate text-sm text-muted">{channel.hint}</span>
              </span>
              <span className="shrink-0 text-muted" aria-hidden>
                ›
              </span>
            </a>
          ))}
        </div>
      )}
    </Sheet>
  );
}
